import { Button, DataTable, PageHead } from "@nabble/ui";
import { useMemo, useState } from "react";
import { fetchOrganizations, type Organization } from "../../../api/adminOrganizations";
import { useAuth } from "../../../auth/AuthContext";
import { useLoadedData } from "../../../hooks/useLoadedData";
import { DeleteOrganizationDialog } from "./DeleteOrganizationDialog";
import { organizationColumns } from "./organizationColumns";
import { OrganizationDialog } from "./OrganizationDialog";

const NO_ORGANIZATIONS: Organization[] = [];

/** Organizations group people: everyone belongs to one, and works and chats with its members. */
export default function AdminOrganizationsPage(): React.JSX.Element {
  const { user: me } = useAuth();
  const { data, loading, loadError, reload } = useLoadedData(fetchOrganizations, "We couldn't load the organizations.");
  const [editing, setEditing] = useState<Organization | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);

  const columns = useMemo(
    () =>
      organizationColumns({
        belongsTo: (o) => me?.organizations.some((m) => m.id === o.id) ?? false,
        rename: setEditing,
        remove: setDeleteTarget,
      }),
    [me],
  );

  return (
    <div>
      <PageHead titleHidden title="Organizations" actions={<Button label="Create organization" icon="plus" onClick={() => setEditing("new")} />} />

      <DataTable<Organization>
        id="admin-organizations"
        rowKey="id"
        loading={loading}
        rows={data ?? NO_ORGANIZATIONS}
        columns={columns}
        exportName="organizations"
        searchPlaceholder="Search organizations"
        defaultSort={{ key: "name", dir: "asc" }}
        onRowClick={(o) => setEditing(o)}
        error={
          loadError
            ? {
                title: "We couldn't load the organizations",
                description: loadError,
                action: <Button label="Try again" variant="secondary" icon="refresh" onClick={reload} />,
              }
            : null
        }
        empty={{
          icon: "briefcase",
          title: "No organizations yet",
          description: "Create an organization, then invite its people from Users.",
          action: <Button label="Create organization" icon="plus" onClick={() => setEditing("new")} />,
        }}
      />

      {editing ? (
        <OrganizationDialog
          organization={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}

      <DeleteOrganizationDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          reload();
        }}
      />
    </div>
  );
}
